/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import router from "next/router";
import { toast } from "react-toastify";

import {
  AssetType,
  AuthResponse,
  BasePlateSnapshot,
  BlockSnapshot,
  ExploreFilter,
  ExploreMode,
  JobStatus,
  Like,
  Message,
  Notification as BackendNotification,
  Plate as PlateInterface,
  PlateMetadata,
  PlateSnapshot,
  PublicUser,
  PublicUserWithFollows,
  UserInputContext,
} from "@vienna/common";
import { FileHeaders } from "@vienna/common/src";
import { Invite, User as PrismaUser } from "@vienna/prisma";

import { CurrentUserSnapshot } from "../user/user.interfaces";
import { HTTPMethods } from "src/classes/api/HTTPMethods";
import { Block } from "src/classes/block";
import { BlockFactory } from "src/classes/block/BlockFactory";
import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { Plate } from "src/classes/plate/Plate";
import { BaseUser, CurrentUser } from "src/classes/user";
import { UserConverter } from "src/classes/user/UserConverter";
import { Toaster } from "src/classes/utilities/Toaster";
import { DrilledBlockData, DrilledPlateData } from "src/components/DrilledView/drilled.interfaces";
import { TOAST_DEFAULT_ARGS } from "src/lib/constants";
import { CreatePlateDto } from "src/lib/dto";
import { Notification } from "src/lib/types";

export enum APIRoute {
  Plates = "plates",
  ConnectPlate = "plates/connect",
  SlashPlate = "plates/plate",
  LikePlate = "plates/like",
  LikedPlates = "plates/liked",
  SharedPlates = "plates/shared",
  ExamplePlates = "plates/examples",
  UserPlates = "plates/user",
  LikedBlocks = "blocks/liked",
  SignedURL = "files/signed-url",
  DownloadFile = "files/download",
  Explore = "explore",
  Notifications = "notifications",
  Blocks = "blocks",
  DrilledBlock = "blocks/drilled",
  DrilledPlate = "plates/drilled",
  ConnectBlock = "blocks/connect",
  LikeBlock = "blocks/like",
  Users = "users",
  UserProfile = "users/profile",
  Follow = "users/follow",
  SignIn = "auth/sign-in",
  SignInUrbit = "auth/sign-in-urbit",
  Generate = "generations",
  CancelGenerate = "generations/cancel",
  UserInput = "generations/user-input",
  RefreshUser = "auth/refresh-user",
  SendConfirmation = "onboarding/email-password",
  ResendConfirmation = "onboarding/resend-code",
  ConfirmEmail = "onboarding/confirm-code",
  CreateUser = "onboarding/create-user",
  CreateUrbitUser = "onboarding/create-urbit-user",
  UpdateUser = "onboarding/update-user",
  VerifyMnemonic = "onboarding/invite",
  CompleteOnboarding = "onboarding/complete",
  ConfirmUrbitKey = "onboarding/confirm-key",
  RequestPasswordReset = "auth/request-password-reset",
  ResetPassword = "auth/reset-password",
  UpdateEditors = "plates/share",
  UsernameAvailability = "users/username",
  CreateInvite = "invites",
}

/* -------------------------------------------------- */
/*                       PLATE                        */
/* -------------------------------------------------- */
class PlateAPI {
  private static base = new HTTPMethods();

  public async create(data: CreatePlateDto): Promise<Plate | undefined> {
    try {
      const snapshot = await PlateAPI.base.post<CreatePlateDto, PlateSnapshot>(APIRoute.Plates, data);
      const plate = Plate.fromSnapshot(snapshot!);
      Toaster.success(
        `Created ${plate.snapshot.isPublic && "public"} plate ${plate.snapshot.title}`,
        plate.internalLink
      );
      return plate;
    } catch {
      return;
    }
  }

  public async get(username: string, title: string): Promise<Plate> {
    const snapshot = await PlateAPI.base.get<
      {
        username: string;
        title: string;
      },
      PlateSnapshot
    >(APIRoute.SlashPlate, { username, title });
    return Plate.fromSnapshot(snapshot);
  }

  public async update(plateId: string, updates: { isPublic?: boolean; height?: number }): Promise<void> {
    await PlateAPI.base.patch(APIRoute.Plates, { id: plateId, dto: updates });
  }

  public async delete(id: string, title: string): Promise<void> {
    await PlateAPI.base.delete<{}>(APIRoute.Plates, { id });
    router.push("/home/mine");
    toast(`Deleted: ${title}`, TOAST_DEFAULT_ARGS);
  }

  public async getDrilled(id: string): Promise<DrilledPlateData> {
    const res = await PlateAPI.base.get<
      { id: string },
      { plate: BasePlateSnapshot; plates: BasePlateSnapshot[]; likes: Like[]; owner: PublicUser }
    >(APIRoute.DrilledPlate, { id });
    const plate = new BasePlate(res.plate);
    const plates = res.plates.map((snapshot) => new BasePlate(snapshot));
    const owner = BaseUser.fromBackendUser(res.owner);
    const likedBy = res.likes.map((like) => BaseUser.fromBackendUser(like.user!));
    return { plate, plates, likedBy, owner };
  }

  public async getExample(): Promise<BasePlate[]> {
    const snapshots = await PlateAPI.base.get<{}, BasePlateSnapshot[]>(APIRoute.ExamplePlates);
    return snapshots.map((snapshot) => new BasePlate(snapshot));
  }

  public async connect(fromPlateId: string, toPlate: BasePlate | Plate): Promise<void> {
    try {
      await PlateAPI.base.post<{ fromPlateId: string; toPlateId: string }, void>(APIRoute.ConnectPlate, {
        fromPlateId,
        toPlateId: toPlate.snapshot.id,
      });
      Toaster.success(`Added plate to ${toPlate.snapshot.title}`, toPlate.internalLink);
    } catch {
      return;
    }
  }

  public async like(plateId: string): Promise<void> {
    await PlateAPI.base.post<{}, void>(APIRoute.LikePlate, { plateId });
  }

  public async generate(
    topic: string,
    assetTypes: AssetType[],
    jobId: string
  ): Promise<{ status: JobStatus; messages: Message[]; errorMessage?: string | null }> {
    const body = { topic, assetTypes, jobId };
    const res = await PlateAPI.base.post<
      { topic: string; assetTypes: AssetType[]; jobId: string },
      { status: JobStatus; messages: Message[]; errorMessage?: string | null }
    >(APIRoute.Generate, body);
    return res;
  }

  public async cancelJob(jobId: string): Promise<{ status: JobStatus; errorMessage?: string | null }> {
    const body = { jobId };
    const res = await PlateAPI.base.post<{ jobId: string }, { status: JobStatus; errorMessage?: string | null }>(
      APIRoute.CancelGenerate,
      body
    );
    return res;
  }

  public async handleArbitraryInput(input: string, context: UserInputContext, jobId: string): Promise<void> {
    const body = { input, context, jobId };
    await PlateAPI.base.post<{ input: string; context: UserInputContext; jobId: string }, void>(
      APIRoute.UserInput,
      body
    );
  }

  public async updateEditors(plateId: string, editorId: string, action: "ADD" | "REMOVE"): Promise<PlateMetadata> {
    const res = await PlateAPI.base.post<{ id: string; editor: string; action: "ADD" | "REMOVE" }, PlateMetadata>(
      APIRoute.UpdateEditors,
      { id: plateId, editor: editorId, action }
    );
    return res;
  }

  public async updateSettings(plateId: string, updates: Partial<PlateInterface>): Promise<PlateMetadata> {
    const res = await PlateAPI.base.patch<{ id: string; dto: Partial<PlateInterface> }, PlateMetadata>(
      APIRoute.Plates,
      {
        id: plateId,
        dto: updates,
      }
    );
    if (!res) {
      throw new Error("Failed to update plate settings");
    }
    return res;
  }
}

/* -------------------------------------------------- */
/*                       BLOCK                        */
/* -------------------------------------------------- */
class BlockAPI {
  private static base = new HTTPMethods();

  public async like(blockId: string): Promise<void> {
    await BlockAPI.base.post<{ blockId: string }, void>(APIRoute.LikeBlock, { blockId });
  }

  public async getLiked(cursor: string | null): Promise<{ blocks: Block[]; cursor: string }> {
    const res = await BlockAPI.base.get<{ cursor: string | null }, { likes: BlockSnapshot[]; cursor: string }>(
      APIRoute.LikedBlocks,
      {
        cursor,
      }
    );
    const blocks = res.likes.map((snapshot) => BlockFactory.create(snapshot, null)).filter((exists) => exists);
    return { blocks, cursor: res.cursor };
  }

  public async connect(blockId: string, toPlate: BasePlate | Plate): Promise<void> {
    try {
      await BlockAPI.base.post<{ blockId: string; plateId: string }, void>(APIRoute.ConnectBlock, {
        blockId,
        plateId: toPlate.snapshot.id,
      });
      Toaster.success(`Added block to ${toPlate.snapshot.title}`, toPlate.internalLink);
    } catch {
      return;
    }
  }

  public async getExplore(
    mode: ExploreMode,
    filter: ExploreFilter,
    cursor: string | null
  ): Promise<{ blocks: Block[]; cursor: string | null }> {
    const res = await BlockAPI.base.get<
      { filter: ExploreFilter; cursor: string | null; mode: ExploreMode },
      { blocks: BlockSnapshot[]; cursor: string | null }
    >(APIRoute.Explore, { filter, cursor, mode });
    const blocks = res.blocks.map((snapshot) => BlockFactory.create(snapshot, null));
    return { blocks, cursor: res.cursor };
  }

  public async getDrilled(id: string): Promise<DrilledBlockData> {
    const res = await BlockAPI.base.get<
      { id: string },
      { block: BlockSnapshot; plates: PlateSnapshot[]; likes: Like[]; owner: PublicUser }
    >(APIRoute.DrilledBlock, { id });
    const block = BlockFactory.create(res.block, null);
    const plates = res.plates.map((snapshot) => new BasePlate(snapshot));
    const blockOwner = BaseUser.fromBackendUser(res.owner);
    const likedBy = res.likes.map((like) => BaseUser.fromBackendUser(like.user!));
    return { block, plates, likedBy, owner: blockOwner };
  }
}

class FileAPI {
  private static base = new HTTPMethods();

  public async download(url: string): Promise<{ data: string; headers: FileHeaders }> {
    const response = await FileAPI.base.get<{ url: string }, { data: string; headers: FileHeaders }>(
      APIRoute.DownloadFile,
      {
        url,
      }
    );
    return response;
  }

  public async getSignedURL(filename: string, bucketURL: string): Promise<string> {
    const { signedUrl } = await FileAPI.base.get<{ filename: string; bucket: string }, { signedUrl: string }>(
      APIRoute.SignedURL,
      {
        filename,
        bucket: bucketURL,
      }
    );
    return signedUrl;
  }
}

/* -------------------------------------------------- */
/*                        USER                        */
/* -------------------------------------------------- */
class UserAPI {
  private static base = new HTTPMethods();

  public async get(username: string): Promise<BaseUser> {
    const user = await UserAPI.base.get<{ username: string }, PublicUserWithFollows>(APIRoute.UserProfile, {
      username,
    });
    return BaseUser.fromBackendUser(user);
  }

  public async getLikedPlates(): Promise<BasePlate[]> {
    const snapshots = await UserAPI.base.get<{}, BasePlateSnapshot[]>(APIRoute.LikedPlates);
    return snapshots.map((snapshot) => new BasePlate(snapshot));
  }

  public async getPlates(): Promise<BasePlate[]> {
    const snapshots = await UserAPI.base.get<{}, BasePlateSnapshot[]>(APIRoute.Plates);
    return snapshots.map((snapshot) => new BasePlate(snapshot));
  }

  public async getSharedPlates(): Promise<BasePlate[]> {
    const snapshots = await UserAPI.base.get<{}, BasePlateSnapshot[]>(APIRoute.SharedPlates);
    return snapshots.map((snapshot) => new BasePlate(snapshot));
  }

  public async getPlatesByUsername(username: string): Promise<BasePlate[]> {
    const snapshots = await UserAPI.base.get<{ username: string }, BasePlateSnapshot[]>(APIRoute.UserPlates, {
      username,
    });
    return snapshots.map((snapshot) => new BasePlate(snapshot));
  }

  public async getAll(): Promise<BaseUser[]> {
    const users = await UserAPI.base.get<{}, PublicUser[]>(APIRoute.Users);
    return users.map((user) => BaseUser.fromBackendUser(user));
  }

  public async checkUsernameAvailability(username: string): Promise<{ availability: string }> {
    const res = await UserAPI.base.get<{ username: string }, { availability: string }>(
      APIRoute.UsernameAvailability,
      {
        username,
      }
    );
    return res;
  }

  public async createInvite(daysValid: number, hint?: string): Promise<Invite> {
    const res = await UserAPI.base.post<{ daysValid: number; hint?: string }, Invite>(APIRoute.CreateInvite, {
      daysValid,
      hint,
    });
    return res;
  }

  /* -------------------------------------------------- */
  /*                      Follow                        */
  /* -------------------------------------------------- */

  public async follow(userId: string): Promise<void> {
    await UserAPI.base.post<{}, void>(APIRoute.Follow, { userId });
  }

  /* -------------------------------------------------- */
  /*                     Update                         */
  /* -------------------------------------------------- */

  public async updateProfile(
    id: string,
    updates: Pick<CurrentUserSnapshot, "bio" | "displayName" | "avatarName">
  ): Promise<void> {
    const data = { bio: updates.bio, displayName: updates.displayName, avatarURL: updates.avatarName };
    await UserAPI.base.put(APIRoute.Users, { id, dto: data });
  }
}

class AuthAPI {
  private static base = new HTTPMethods();

  public async getCurrentUser(): Promise<{ token: string; user: CurrentUser }> {
    const res = await AuthAPI.base.post<
      {},
      { token: string; user: PrismaUser; algoliaApiKey: string; plates: BasePlateSnapshot[] }
    >(APIRoute.RefreshUser, {});

    const snapshot = UserConverter.toCurrentUserSnapshot(
      res.user,
      res.algoliaApiKey,
      res.plates.map((plate) => new BasePlate(plate))
    );
    const user = CurrentUser.fromSnapshot(snapshot);
    return { token: res.token, user };
  }

  public async signIn(email: string, password: string): Promise<string> {
    const res = await AuthAPI.base.post<
      { email: string; password: string },
      { token: string; user: PrismaUser; algoliaApiKey: string }
    >(APIRoute.SignIn, { email, password });
    return res.token;
  }

  public async signInWithUrbit(shipName: string, privateKey: string): Promise<string> {
    const res = await AuthAPI.base.post<
      { shipName: string; privateKey: string },
      { token: string; user: PrismaUser; algoliaApiKey: string }
    >(APIRoute.SignInUrbit, { shipName, privateKey });
    return res.token;
  }

  public async verifyMnemonic(mnemonic: string): Promise<{ token: string }> {
    const token = await AuthAPI.base.post<{ mnemonic: string }, { token: string }>(APIRoute.VerifyMnemonic, {
      mnemonic,
    });
    return token;
  }

  public async sendConfirmationEmail(email: string, password: string): Promise<{ token: string }> {
    const { token } = await AuthAPI.base.post<{ email: string; password: string }, { token: string }>(
      APIRoute.SendConfirmation,
      { email, password }
    );
    return { token };
  }

  public async resendConfirmationEmail(): Promise<{ token: string }> {
    const { token } = await AuthAPI.base.post<{}, { token: string }>(APIRoute.ResendConfirmation, {});
    return { token };
  }

  public async confirmEmail(code: string): Promise<{ token: string }> {
    const { token } = await AuthAPI.base.post<{ code: string }, { token: string }>(APIRoute.ConfirmEmail, {
      code,
    });
    return { token };
  }

  public async createAccount(username: string): Promise<{ token: string }> {
    const { token } = await AuthAPI.base.post<{ username: string }, { token: string }>(APIRoute.CreateUser, {
      username,
    });
    return { token };
  }

  public async updateUser(updates: Partial<PrismaUser>): Promise<{ token: string }> {
    const { token } = await AuthAPI.base.post<Partial<PrismaUser>, { token: string }>(
      APIRoute.UpdateUser,
      updates
    );
    return { token };
  }

  public async completeOnboarding(): Promise<string> {
    const res = await AuthAPI.base.post<{}, { token: string; username: string }>(APIRoute.CompleteOnboarding, {});
    return res.token;
  }

  public async createUrbitUser(shipName: string): Promise<{ token: string; privateKey: string }> {
    const res = await AuthAPI.base.post<{ shipName: string }, { token: string; privateKey: string }>(
      APIRoute.CreateUrbitUser,
      { shipName }
    );
    return res;
  }

  public async confirmUrbitKey(privateKey: string): Promise<{ token: string }> {
    const { token } = await AuthAPI.base.post<{ privateKey: string }, { token: string }>(
      APIRoute.ConfirmUrbitKey,
      { privateKey }
    );
    return { token };
  }

  public async requestPasswordReset(email: string): Promise<{ email: string; codeHash: string }> {
    const res = await AuthAPI.base.post<{ email: string }, { email: string; codeHash: string }>(
      APIRoute.RequestPasswordReset,
      { email }
    );
    return res;
  }

  public async resetPassword(body: {
    email: string;
    code: string;
    codeHash: string;
    password: string;
  }): Promise<AuthResponse> {
    const res = await AuthAPI.base.post<
      { email: string; code: string; codeHash: string; password: string },
      AuthResponse
    >(APIRoute.ResetPassword, body);
    return res;
  }
}

class NotificationsAPI {
  private static base = new HTTPMethods();

  //@todo gross conversion
  public async get(): Promise<Notification[]> {
    const notifications = await NotificationsAPI.base.get<{}, BackendNotification[]>(APIRoute.Notifications);
    return notifications.map((notification) => {
      const user = BaseUser.fromBackendUser(notification.user);
      if ((notification as any).plate) {
        const plate = new BasePlate((notification as any).plate!);
        return { ...notification, plate, user } as Notification;
      }
      if ((notification as any).block) {
        const block = BlockFactory.create((notification as any).block!, null);
        return { ...notification, block, user } as Notification;
      }
      return { ...notification, user } as Notification;
    });
  }
}

export class API {
  public static plates = new PlateAPI();

  public static files = new FileAPI();

  public static blocks = new BlockAPI();

  public static users = new UserAPI();

  public static auth = new AuthAPI();

  public static notifications = new NotificationsAPI();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {} // only static methods, can't create an instance hence empty constructor
}
